import * as React from 'react';

import SVGIcon from '../../../Icon/SVGIcon';
import Box, { IBoxProps } from '../../Layout/Box';
import { useFocusControl, useHoverControl } from '../../Utils';
import { IInputAdornment, IInputError, IInputIconProps, IInputMaxWidth, IInputProps } from '../common';
import { useInputControlContext } from '../InputControl/InputControlContext';

import { InputContainer, StyledInput } from './InputStyles';

type InputProps = IInputIconProps & IInputProps & IInputMaxWidth & IInputAdornment & IInputError;

export interface IAdornmentWidth {
  start?: number;
  end?: number;
}

const ADORNMENT_BOX_STYLES: IBoxProps = {
  height: '100%',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 'middle',
};

// exporting here is a work around for this issue https://github.com/microsoft/tslint-microsoft-contrib/issues/667
export const Input = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement> & InputProps>((
  props, ref
) => {
  const {
    adornment,
    iconProps,
    id,
    inputProps,
    error,
    maxWidth
  } = props;
  const startAdornmentRef = React.useRef<HTMLDivElement>(null);
  const endAdornmentRef = React.useRef<HTMLDivElement>(null);
  const [ adornmentWidth, setAdornmentWidth ] = React.useState<IAdornmentWidth>({});
  const iconPositionSite = iconProps && iconProps.position ? iconProps.position : 'left';
  const iconSize = iconProps && iconProps.size;
  const isDisabled = !!(inputProps && inputProps.disabled);
  const { clickFocus, stopClickFocus, resetClickFocus } = useFocusControl();
  const { isHovered, toggleHover } = useHoverControl();
  const toggleInputHover = isDisabled ? undefined : toggleHover;
  const { error: errorContext, ariaDescribedby } = useInputControlContext();
  const ariaDescribedbyWithInputProps = inputProps && inputProps['aria-describedby'] ?
    `${ariaDescribedby} ${inputProps['aria-describedby']}` : ariaDescribedby;

  const handleOnBlur: (e: React.FocusEvent<HTMLInputElement>) => void = e => {
    resetClickFocus();
    if (inputProps && inputProps.onBlur) {
      inputProps.onBlur(e);
    }
  };

  React.useLayoutEffect(
    () => {
      if (startAdornmentRef.current) {
        setAdornmentWidth({...adornmentWidth, start: startAdornmentRef.current.clientWidth});
      }

      if (endAdornmentRef.current) {
        setAdornmentWidth({...adornmentWidth, end: endAdornmentRef.current.clientWidth});
      }
    },
    []
  );

  return (
    <InputContainer
      iconPosition={iconPositionSite}
      iconSize={iconSize}
      isHovered={isHovered}
      onMouseEnter={toggleInputHover}
      onMouseLeave={toggleInputHover}
      maxWidth={maxWidth}
    >
     {/*
        we allow icons and adornments to be render together but stop
        the ability to render them on top of one another
        icons have the priority
      */}
      {adornment && adornment.start && (!iconProps || iconProps.position === 'right') && (
        <Box ref={startAdornmentRef} {...ADORNMENT_BOX_STYLES} position={{ type: 'absolute', left: '0' }}>{adornment.start}</Box>
      )}

      {iconProps && iconProps.icon && (
        <SVGIcon icon={iconProps.icon} />
      )}

      <StyledInput
        {...inputProps}
        aria-describedby={ariaDescribedbyWithInputProps ? ariaDescribedbyWithInputProps : undefined}
        error={error || errorContext}
        hasIcon={iconProps && !!iconProps.icon}
        iconSize={iconSize}
        iconPosition={iconPositionSite}
        id={id}
        isHovered={isHovered}
        ref={ref}
        onBlur={handleOnBlur}
        onMouseDown={stopClickFocus}
        clickFocus={clickFocus}
        adornmentWidth={adornmentWidth}
        hasAdornment={!!adornment}
      />

      {adornment && adornment.end && iconPositionSite !== 'right' && (
        <Box ref={endAdornmentRef} {...ADORNMENT_BOX_STYLES} position={{ type: 'absolute', right: '0' }}>{adornment.end}</Box>
      )}
    </InputContainer>
  );
});

export default Input;
